import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Building } from '../elements/Building';
import { Garden } from '../elements/Garden';
import './DrawingArea.css';

const GRID_SIZE = 40;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
const PADDING = GRID_SIZE * 4; // Padding around elements

const DrawingArea = ({
  elements,
  currentType,
  onElementCreate,
  onElementUpdate,
  onRotate,
  onDelete,
  plotSize
}) => {
  const containerRef = useRef(null);
  const drawingRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [dimensions, setDimensions] = useState({ width: plotSize.width, height: plotSize.height });

  // Calculate dimensions based on elements and plot size
  const calculateDimensions = useCallback(() => {
    // Start with minimum dimensions from plot size
    let maxX = plotSize.width;
    let maxY = plotSize.height;

    // Check all elements
    elements.forEach(element => {
      const elementRight = element.x + element.width;
      const elementBottom = element.y + element.height;
      
      maxX = Math.max(maxX, elementRight);
      maxY = Math.max(maxY, elementBottom);
    });

    // Add padding and ensure minimum size
    return {
      width: Math.max(maxX + PADDING, plotSize.width),
      height: Math.max(maxY + PADDING, plotSize.height)
    };
  }, [elements, plotSize]);

  // Update dimensions when elements or plot size change
  useEffect(() => {
    const newDimensions = calculateDimensions();
    setDimensions(newDimensions);
  }, [elements, plotSize, calculateDimensions]);

  // Handle zoom
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom(prev => Math.min(Math.max(prev + delta, MIN_ZOOM), MAX_ZOOM));
    }
  }, []);

  // Snap coordinates to grid
  const snapToGrid = useCallback((value) => {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  }, []);

  // Convert screen coordinates to drawing area coordinates
  const getDrawingCoordinates = useCallback((clientX, clientY) => {
    const rect = drawingRef.current.getBoundingClientRect();
    const x = (clientX - rect.left - pan.x) / zoom;
    const y = (clientY - rect.top - pan.y) / zoom;
    return { x: snapToGrid(x), y: snapToGrid(y) };
  }, [pan, zoom, snapToGrid]);

  // Handle element creation
  const handleClick = useCallback((e) => {
    if (isSpacePressed || isDragging || !currentType) return;

    const { x, y } = getDrawingCoordinates(e.clientX, e.clientY);
    onElementCreate(x, y, currentType);
  }, [currentType, isDragging, isSpacePressed, getDrawingCoordinates, onElementCreate]);

  // Pan handlers
  const handleKeyDown = useCallback((e) => {
    if (e.code === 'Space' && !isSpacePressed) {
      setIsSpacePressed(true);
      if (containerRef.current) {
        containerRef.current.style.cursor = 'grab';
      }
    }
  }, [isSpacePressed]);

  const handleKeyUp = useCallback((e) => {
    if (e.code === 'Space') {
      setIsSpacePressed(false);
      if (containerRef.current) {
        containerRef.current.style.cursor = 'default';
      }
    }
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (isSpacePressed) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      if (containerRef.current) {
        containerRef.current.style.cursor = 'grabbing';
      }
    }
  }, [isSpacePressed, pan]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && dragStart) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = isSpacePressed ? 'grab' : 'default';
    }
  }, [isSpacePressed]);

  // Setup event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Render elements
  const renderElements = useCallback(() => {
    return elements.map((element) => {
      const props = {
        ...element,
        onRotate,
        onDelete,
      };

      switch (element.category) {
        case 'building':
          return <Building key={element.id} {...props} />;
        case 'garden':
          return <Garden key={element.id} {...props} />;
        default:
          return null;
      }
    });
  }, [elements, onRotate, onDelete]);

  return (
    <div
      ref={containerRef}
      className="drawing-area-container"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        ref={drawingRef}
        className="drawing-area"
        onClick={handleClick}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
          backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`
        }}
      >
        <div className="elements-layer">
          {renderElements()}
        </div>
      </div>
      
      <div className="zoom-controls">
        <button onClick={() => setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM))}>+</button>
        <span>{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM))}>-</button>
      </div>
    </div>
  );
};

export default DrawingArea;